import { Fragment, useId, type CSSProperties, type ReactNode } from 'react';
import { HeartIcon, SteeringWheelIcon, XIcon } from '@phosphor-icons/react';
import type {
  BusSeatClassGlyph,
  BusSeatFloorView,
  BusSeatView,
  BusSegmentPick,
} from '../models.js';
import { BusSeatGlyph, type BusSeatLegendCopy } from './bus_seat_glyph.js';
import {
  busSeatArea,
  busSeatDeck,
  busSeatTraits,
  type BusSeatArea,
  type BusSeatTraits,
} from './bus_seat_rules.js';

/** Quem viaja, como a planta mostra: as iniciais na poltrona e o nome no
 *  detalhe. */
export type BusSeatTraveler = {
  readonly name: string;
  readonly initials: string;
};

/** O detalhe de uma poltrona e a legenda. Opcional no host: sem ele, a planta
 *  não mostra a legenda, a régua dos terços nem a linha de detalhe. */
type BusSeatDetailsCopy = {
  legend: BusSeatLegendCopy;
  window: string;
  aisle: string;
  /** A poltrona sozinha do 2+1: janela e corredor ao mesmo tempo. */
  windowAndAisle: string;
  individual: string;
  /** "Frente", "Meio", "Fundo" — na régua e no detalhe. */
  area(area: BusSeatArea): string;
  neighborFree: string;
  neighborOccupied: string;
  recommended: string;
  /** A linha antes de qualquer poltrona ("Toque numa poltrona para ver os detalhes"). */
  hint: string;
};

/** Os rótulos que a planta usa. */
export type BusSeatDeckCopy = {
  /** O rótulo vertical na frente do ônibus ("Entrada"). */
  entranceLabel: string;
  /** O nome do volante, só para leitor de tela ("Motorista"). */
  driverLabel: string;
  /** "Poltrona 14" / "Poltrona 14, ocupada" — o nome do botão. */
  seatLabel(code: string, occupied: boolean): string;
  details?: BusSeatDetailsCopy;
};

/** O que a planta marca em cima das poltronas: de quem é cada escolhida e
 *  quais as Preferências recomendam. */
export type SeatMarks = {
  readonly owners: ReadonlyMap<string, BusSeatTraveler | undefined>;
  readonly recommended: ReadonlySet<string>;
};

/** O que todas as poltronas de um trecho compartilham. */
export type SeatDeckContext = {
  readonly layout: 'inline' | 'sheet';
  readonly glyph: BusSeatClassGlyph;
  readonly marks: SeatMarks;
  readonly copy: BusSeatDeckCopy;
  readonly disabled: boolean;
  readonly onToggle: (seat: BusSeatView) => void;
  readonly onInspect: (code: string) => void;
};

/** As marcas do trecho: o dono de cada poltrona escolhida (pelo `travelerId`)
 *  e as poltronas da sugestão. */
export function seatMarks(
  pick: BusSegmentPick | undefined,
  recommended: BusSegmentPick | undefined,
  travelers: Readonly<Record<string, BusSeatTraveler>> | undefined,
): SeatMarks {
  const chosen = pick?.kind === 'seats' ? Object.entries(pick.seats) : [];
  return {
    owners: new Map(chosen.map(([id, code]) => [code, travelers?.[id]])),
    recommended: new Set(recommended?.kind === 'seats' ? Object.values(recommended.seats) : []),
  };
}

/** Uma poltrona com tudo o que a planta sabe e marca nela. */
type SeatCellView = {
  readonly seat: BusSeatView;
  readonly traits: BusSeatTraits | undefined;
  /** O deque é um palpite (sem colunas): janela, terço e vizinho não valem. */
  readonly estimated: boolean;
  readonly owner: BusSeatTraveler | undefined;
  readonly selected: boolean;
  readonly recommended: boolean;
};

function cellView(
  seat: BusSeatView,
  traits: BusSeatTraits | undefined,
  estimated: boolean,
  marks: SeatMarks,
): SeatCellView {
  return {
    seat,
    traits,
    estimated,
    owner: marks.owners.get(seat.code),
    selected: marks.owners.has(seat.code),
    recommended: marks.recommended.has(seat.code) && !seat.occupied,
  };
}

/** Uma parte da linha de detalhe. `kind` é a chave do chip (cada tipo aparece
 *  uma vez; o texto pode repetir — um viajante chamado "Meio"); `good` e
 *  `muted` mudam só o tom. */
type DetailPart = {
  readonly kind: 'seat' | 'position' | 'individual' | 'area' | 'neighbor' | 'recommended' | 'owner';
  readonly text: string;
  readonly tone?: 'good' | 'muted';
};

function positionOf(traits: BusSeatTraits, details: BusSeatDetailsCopy): string | undefined {
  if (traits.window && traits.aisle) return details.windowAndAisle;
  if (traits.window) return details.window;
  return traits.aisle ? details.aisle : undefined;
}

function neighborOf(cell: SeatCellView, details: BusSeatDetailsCopy): DetailPart[] {
  const neighbor = cell.seat.occupied ? null : cell.traits?.neighbor;
  if (neighbor === 'free') return [{ kind: 'neighbor', text: details.neighborFree, tone: 'good' }];
  return neighbor === 'occupied'
    ? [{ kind: 'neighbor', text: details.neighborOccupied, tone: 'muted' }]
    : [];
}

/** O que se diz de uma poltrona: o nome, a posição, o terço, o vizinho, se é
 *  a recomendada e de quem é. No deque estimado, só o que não é palpite. */
function seatDetailParts(cell: SeatCellView, copy: BusSeatDeckCopy): DetailPart[] {
  const { details } = copy;
  if (!details) return [];
  const { seat, traits } = cell;
  const known = traits && !cell.estimated ? traits : undefined;
  const position = known && positionOf(known, details);
  return [
    { kind: 'seat', text: copy.seatLabel(seat.code, seat.occupied) },
    ...(position ? [{ kind: 'position' as const, text: position }] : []),
    ...(known?.individual ? [{ kind: 'individual' as const, text: details.individual }] : []),
    ...(known ? [{ kind: 'area' as const, text: details.area(known.area) }] : []),
    ...(known ? neighborOf(cell, details) : []),
    ...(cell.recommended
      ? [{ kind: 'recommended' as const, text: details.recommended, tone: 'good' as const }]
      : []),
    ...(cell.owner ? [{ kind: 'owner' as const, text: cell.owner.name }] : []),
  ];
}

/** As iniciais de quem escolheu a poltrona — ou o número, sem dono ou sem
 *  iniciais (um nome vazio não pode apagar a poltrona). */
function markOf(cell: SeatCellView): string {
  const initials = cell.owner?.initials;
  return initials ? initials : cell.seat.code;
}

function seatState(cell: SeatCellView): 'occupied' | 'selected' | 'free' {
  if (cell.seat.occupied) return 'occupied';
  return cell.selected ? 'selected' : 'free';
}

/**
 * Uma poltrona: o glifo 2.5D no estado dela, o número (ou as iniciais de quem
 * a escolheu), o X quando ocupada e o coração quando é a recomendada. O nome
 * do botão é só "Poltrona 14"; o detalhe (janela, terço, vizinho…) vai na
 * descrição, para o leitor de tela e para a linha de detalhe.
 */
function SeatCell({
  cell,
  context,
}: {
  readonly cell: SeatCellView;
  readonly context: SeatDeckContext;
}): ReactNode {
  const { seat } = cell;
  const descriptionId = useId();
  const description = seatDetailParts(cell, context.copy)
    .slice(1)
    .map((part) => part.text)
    .join(', ');
  // A ocupada não entra na linha de detalhe: é `disabled` e o navegador nem
  // sempre entrega o ponteiro a um botão desligado (o Chrome entrega, outros
  // não). Fica igual em todos; o leitor de tela segue com a descrição.
  const inspect = () => {
    if (!seat.occupied) context.onInspect(seat.code);
  };
  return (
    <button
      type="button"
      className="hui-bus-seat"
      data-state={seatState(cell)}
      data-recommended={cell.recommended ? 'true' : undefined}
      aria-label={context.copy.seatLabel(seat.code, seat.occupied)}
      aria-describedby={description ? descriptionId : undefined}
      aria-pressed={cell.selected}
      disabled={context.disabled || seat.occupied}
      onClick={() => {
        context.onToggle(seat);
        inspect();
      }}
      onFocus={inspect}
      onMouseEnter={inspect}
    >
      <BusSeatGlyph shape={context.glyph} layout={context.layout} />
      <span className="hui-bus-seat-mark" aria-hidden="true">
        {seat.occupied ? <XIcon /> : markOf(cell)}
      </span>
      {cell.recommended && (
        <span className="hui-bus-seat-heart" aria-hidden="true">
          <HeartIcon weight="fill" />
        </span>
      )}
      {description && (
        <span id={descriptionId} hidden>
          {description}
        </span>
      )}
    </button>
  );
}

/** As janelas do lado de fora: uma marca por fileira. */
function WindowStrip({ rows }: { readonly rows: number }): ReactNode {
  return (
    <div className="hui-bus-seat-windows" aria-hidden="true">
      {Array.from({ length: rows }, (_, row) => (
        <span key={row} />
      ))}
    </div>
  );
}

const AREAS: readonly BusSeatArea[] = ['front', 'middle', 'back'];

/** O corredor — e, quando a planta sabe as colunas, a régua dos terços nele:
 *  cada trecho da régua cobre as fileiras do seu terço (`--hui-rows`). */
function SeatAisle({
  rows,
  details,
}: {
  readonly rows: number;
  readonly details: BusSeatDetailsCopy | undefined;
}): ReactNode {
  const thirds = Array.from({ length: rows }, (_, row) => busSeatArea(row, rows));
  const segments = details
    ? AREAS.map((area) => ({ area, rows: thirds.filter((third) => third === area).length })).filter(
        (segment) => segment.rows > 0,
      )
    : [];
  return (
    <div className="hui-bus-seat-aisle" aria-hidden="true">
      {segments.map((segment) => (
        <span
          key={segment.area}
          className="hui-bus-seat-area"
          style={{ '--hui-rows': segment.rows } as CSSProperties}
        >
          {details?.area(segment.area)}
        </span>
      ))}
    </div>
  );
}

/**
 * Um andar como planta: a frente (Entrada e o volante), as janelas por fora,
 * as faixas das colunas e o corredor entre elas — com a régua Frente / Meio /
 * Fundo. Deitada no card (a frente à esquerda) ou em pé na folha. Sem colunas
 * (o 2+2 estimado), sem janelas e sem régua: a planta não afirma o que não sabe.
 */
export function SeatDeck({
  floor,
  context,
}: {
  readonly floor: BusSeatFloorView;
  readonly context: SeatDeckContext;
}): ReactNode {
  const deck = busSeatDeck(floor);
  const traits = busSeatTraits(deck);
  const rows = Math.max(0, ...deck.lanes.map((lane) => lane.cells.length));
  const known = !deck.estimated && deck.lanes.length > 1;
  const hasAisle = deck.aisleAfter > 0 && deck.aisleAfter < deck.lanes.length;
  return (
    <div className="hui-bus-seat-deck" data-layout={context.layout} data-shape={context.glyph}>
      <div className="hui-bus-seat-front">
        <span className="hui-bus-seat-entrance">{context.copy.entranceLabel}</span>
        <SteeringWheelIcon role="img" aria-label={context.copy.driverLabel} />
      </div>
      <div className="hui-bus-seat-lanes">
        {known && <WindowStrip rows={rows} />}
        {deck.lanes.map((lane, index) => (
          <Fragment key={lane.column}>
            {hasAisle && index === deck.aisleAfter && (
              <SeatAisle rows={rows} details={known ? context.copy.details : undefined} />
            )}
            <div className="hui-bus-seat-lane">
              {lane.cells.map((seat, cell) =>
                seat ? (
                  <SeatCell
                    key={seat.code}
                    cell={cellView(seat, traits.get(seat.code), deck.estimated, context.marks)}
                    context={context}
                  />
                ) : (
                  <span
                    key={`gap-${String(cell)}`}
                    className="hui-bus-seat-gap"
                    aria-hidden="true"
                  />
                ),
              )}
            </div>
          </Fragment>
        ))}
        {known && <WindowStrip rows={rows} />}
      </div>
    </div>
  );
}

/** A poltrona `code` em algum dos andares, com o que a planta sabe dela. */
function findCell(
  floors: readonly BusSeatFloorView[],
  code: string,
  marks: SeatMarks,
): SeatCellView | undefined {
  for (const floor of floors) {
    const deck = busSeatDeck(floor);
    const seat = deck.lanes.flatMap((lane) => lane.cells).find((cell) => cell?.code === code);
    if (seat) return cellView(seat, busSeatTraits(deck).get(code), deck.estimated, marks);
  }
  return undefined;
}

/**
 * A linha de detalhe embaixo da planta: o que se sabe da poltrona sob o
 * ponteiro, em foco ou tocada — "Poltrona 14 · Janela · Meio · Vizinho livre".
 * Só visual (`aria-hidden`): o leitor de tela já ouve o mesmo na descrição do
 * botão. Sem poltrona ainda, a dica.
 */
export function SeatDetails({
  floors,
  code,
  context,
}: {
  readonly floors: readonly BusSeatFloorView[];
  readonly code: string | undefined;
  readonly context: SeatDeckContext;
}): ReactNode {
  const { details } = context.copy;
  if (!details) return null;
  const cell = code === undefined ? undefined : findCell(floors, code, context.marks);
  const parts = cell ? seatDetailParts(cell, context.copy) : [];
  return (
    <p className="hui-bus-seat-details" aria-hidden="true">
      {parts.length === 0 ? (
        <span className="hui-bus-seat-details-hint">{details.hint}</span>
      ) : (
        parts.map((part) => (
          <span key={part.kind} className="hui-bus-seat-chip" data-tone={part.tone}>
            {part.text}
          </span>
        ))
      )}
    </p>
  );
}
